/**
 * Returns the full name composed of the first name and last name.
 * If both first name and last name are undefined, returns the default name if provided.
 * If no default name is provided, returns an empty string.
 *
 * @param {string | undefined} firstName - The first name.
 * @param {string | undefined} lastName - The last name.
 * @param {string} [defaultName] - The default name to return if both first name and last name are undefined.
 */
export const getFullName = (
  firstName: string | undefined,
  lastName: string | undefined,
  defaultName?: string
) => {
  const fullName = [firstName, lastName].filter(Boolean).join(' ');
  return fullName || defaultName || '';
};

type EmployeeNameSource = {
  firstName?: string;
  lastName?: string;
  preferredFirstName?: string;
};

/**
 * Picks the first name field to display. If the employee set a preferred
 * name, use that. Otherwise, use their normal first name.
 */
export const getDisplayFirstName = (
  employee: EmployeeNameSource | null | undefined
) => employee?.preferredFirstName || employee?.firstName;

/**
 * Builds the full name to display (first + last name), using the
 * preferred name if the employee has one.
 *
 * @param {EmployeeNameSource} employee - The employee to build the name from.
 * @param {string} [defaultName] - What to show if the employee has no name at all.
 */
export const getDisplayFullName = (
  employee: EmployeeNameSource | null | undefined,
  defaultName?: string
) =>
  getFullName(getDisplayFirstName(employee), employee?.lastName, defaultName);
